import { Injectable, Injector, NgZone } from '@angular/core';
import { TransitionService } from '@uirouter/core';
import { CommonBaseService } from '@CaseOne/Common/common-base-service.class/common-base-service.class';
import { CommonEventsService } from '@CaseOne/Common/events/services/events.service';
import { CommonPopupService } from '@CaseOne/Common/popup/services/popup.service';
import { ICommonUserNotificationDialog } from '../types/user-notification-dialog.interface';
import { COMMON_USER_NOTIFICATION_DIALOG_EVENTS } from '../types/user-notification-dialog-events.enum';
import { COMMON_USER_NOTIFICATION_DIALOG_COMPONENT } from '../constants/user-notification-dialog-component.token';
import { UUID } from '@CaseOne/Common/interfaces/core';
import { first } from 'rxjs/operators';
import { CommonPopup } from '@CaseOne/Common/popup/popup';


@Injectable({
    providedIn: 'root',
})
export class CommonUserNotificationDialogService extends CommonBaseService {
    private queue: ICommonUserNotificationDialog[] = [];
    private openDialogs: Map<string, CommonPopup> = new Map();
    private projectId: UUID;

    private commonEventsService = this.injector.get(CommonEventsService);
    private transitionService = this.injector.get(TransitionService);
    private commonPopupService = this.injector.get(CommonPopupService);
    private ngZone = this.injector.get(NgZone);

    constructor(injector: Injector) {
        super(injector);

        this.initWebSocket();
        this.initAfterFirstRouting();
    }

    initAfterFirstRouting(): Promise<void> {
        return new Promise((resolve) => {
            this.transitionService.onSuccess({}, (transition) => {
                this.projectId = transition.params().ProjectId;
                resolve();
            });
        });
    }

    handleOpenDialog(data: ICommonUserNotificationDialog[]): void {
        if (!data?.[0]) {
            return;
        }

        const dialog = data[0];

        if (this.isDialogDisplayAllowed(dialog, this.projectId)) {
            this.enqueueDialog(dialog);
            this.openDialog(dialog);
        }
    }

    private initWebSocket(): void {
        this.commonEventsService
            .on(COMMON_USER_NOTIFICATION_DIALOG_EVENTS.CLOSE_DIALOG)
            .pipe(this.takeUntilDestroy())
            .subscribe((dialog: ICommonUserNotificationDialog) => {
                this.ngZone.run(() => this.closeUserNotificationDialog(dialog.Id));
            });
    }

    private isDialogDisplayAllowed(notificationDialog: ICommonUserNotificationDialog, currentObjectId: UUID): boolean {
        if (notificationDialog.DisplayOnlyInObjectCard && !!notificationDialog.ObjectId) {
            // Display is allowed for currentObject
            return notificationDialog.ObjectId === currentObjectId;
        } else if (notificationDialog.DisplayOnlyInObjectCard && !notificationDialog.ObjectId) {
            // Display is allowed for all objects
            return !!currentObjectId;
        } else {
             // Display is allowed for all locations in the system
            return true;
        }
    }

    private openDialog(data: ICommonUserNotificationDialog): void {
        const component = this.injector.get(COMMON_USER_NOTIFICATION_DIALOG_COMPONENT, null);

        if (!component) {
            return;
        }

        const dialogRef = this.commonPopupService.open({
            component,
            popupClasses: 'b-popup-dialog',
            showHeader: false,
            data,
        });

        this.markAsOpen(data.Id, dialogRef);

        dialogRef.onAfterClose()
            .pipe(first())
            .subscribe(() => {
                this.markAsClosed(data.Id);
            });
    }

    private removeFromQueue(id: UUID) {
        this.queue = this.queue.filter((queueDialog: ICommonUserNotificationDialog) => queueDialog.Id !== id);
    }

    private markAsOpen(id: UUID, dialogRef: CommonPopup): void {
        this.openDialogs.set(id, dialogRef);
    }

    private markAsClosed(id: UUID): void {
        this.openDialogs.delete(id);
        this.removeFromQueue(id);
    }

    private enqueueDialog(dialog: ICommonUserNotificationDialog): void {
        this.queue.push(dialog);
    }

    private closeUserNotificationDialog(id: UUID): void {
        const dialogRef = this.openDialogs.get(id);

        if (dialogRef) {
            dialogRef.close();
            this.openDialogs.delete(id);
        }

        this.removeFromQueue(id);
    }
}
